import { NgClass } from '@angular/common';
import { Component, HostListener, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Tag, TagService } from '@frontend/core';
import { InsightMarkdownService, PaginationModule } from '@frontend/shared';
import { TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ContentItem } from '../../content/content-item.model';
import { CardsModule } from '../../content/cards/cards.module';
import { Topic } from '../../content/topics/topic.model';
import { TopicService } from '../../content/topics/topics.service';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import { WindowService } from '../../utilities';
import { hasChosenFilter, mayChoose, newsFeedQueryParams, readNewsFeedState, toggled } from '../news-feed-state';
import { NewsFeedDefinition, NewsFeedState, NewsItem, NewsPage, NewsRelation, NewsSort } from '../news.model';
import { NewsService } from '../news.service';

const PAGE_SIZE = 10;
const FEATURED_SHOWN = 3;
const LISTING_RELATIONS: NewsRelation[] = ['topics', 'tags', 'images', 'videos'];

/**
 * One news feed: its featured items, its paginated list, and the filters its
 * reader may change. Everything the reader chooses lives in the URL; this
 * component reads its state from the query string and changes it only by
 * navigating, so a link or a reload rebuilds the same page.
 *
 * Which items the feed shows, and what the reader may change, come from the
 * route's `data.newsFeed` — see `NewsFeedDefinition`.
 *
 * Built from the tips listing's elements: the feed-with-filters layout, the
 * horizontal CTA card, pagination type three and the filter panel.
 */
@Component({
  selector: 'multisite-news-feed',
  templateUrl: './news-feed.component.html',
  imports: [
    NgClass,
    FormsModule,
    RouterModule,
    TranslocoModule,
    AlertModule,
    CollapseModule,
    PaginationModule,
    CardsModule,
    LoaderGlobeAnimationDetailComponent,
  ],
})
export class NewsFeedComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private newsService = inject(NewsService);
  private tagService = inject(TagService);
  private topicService = inject(TopicService);
  private loadingService = inject(LoadingService);
  private markdown = inject(InsightMarkdownService);
  private windowService = inject(WindowService);

  readonly feed: NewsFeedDefinition = this.route.snapshot.data['newsFeed'];

  readonly state = toSignal(this.route.queryParamMap.pipe(map((params) => readNewsFeedState(params, this.feed))), {
    requireSync: true,
  });

  newsPage = signal<NewsPage | null>(null);
  featured = signal<NewsItem[]>([]);
  applications = signal<Topic[]>([]);
  tagGroups = signal<{ type: string; tags: Tag[] }[]>([]);
  failed = signal(false);
  searchText = '';
  areFiltersCollapsed = signal(false);

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  cards = computed(() => (this.newsPage()?.items ?? []).map((item) => this.toContentItem(item)));
  featuredCards = computed(() => this.featured().map((item) => this.toContentItem(item)));
  hasChosenFilter = computed(() => hasChosenFilter(this.state(), this.feed));

  readonly mayChooseApplication = mayChoose(this.feed, 'application');
  readonly mayChooseSearch = mayChoose(this.feed, 'search');
  readonly mayChooseSort = mayChoose(this.feed, 'sort');
  readonly sorts: { sort: NewsSort; translationKey: string }[] = [
    { sort: '-published_at', translationKey: 'common.newest_first' },
    { sort: 'published_at', translationKey: 'common.oldest_first' },
  ];

  private readonly maxWidthMediaQuery = '(max-width: 767px)';
  private loads: Subscription[] = [];
  private filterLoads: Subscription[] = [];

  @HostListener('window:resize')
  resizeWindow() {
    this.refreshCollapse();
  }

  constructor() {
    this.refreshCollapse();

    effect(() => {
      const state = this.state();
      untracked(() => {
        // Back and forward change the URL without the form: follow it.
        this.searchText = state.filter.search ?? '';
        this.load(state);
      });
    });

    // Every service read here caches per locale, so a language already visited
    // costs no request for the filters; the news itself is asked for again.
    reloadOnLocaleChange(() => {
      this.load(this.state());
      this.loadFilters();
    });

    this.loadFilters();
  }

  setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }

  isApplicationChosen(slug: string): boolean {
    return (this.state().filter.application ?? []).includes(slug);
  }

  isTagChosen(tag: Tag): boolean {
    return (this.state().filter.tag ?? []).includes(tag.type + ':' + tag.slug);
  }

  chooseApplication(slug: string, on: boolean): void {
    const application = toggled(this.state().filter.application, slug, on, this.feed.defaultFilter.application);
    this.navigate({ ...this.state(), filter: { ...this.state().filter, application }, page: 1 });
  }

  chooseTag(tag: Tag, on: boolean): void {
    const chosen = toggled(this.state().filter.tag, tag.type + ':' + tag.slug, on, this.feed.defaultFilter.tag);
    this.navigate({ ...this.state(), filter: { ...this.state().filter, tag: chosen }, page: 1 });
  }

  search(): void {
    const text = this.searchText.trim();
    if (text.length > 0 && text.length < 3) {
      return;
    }

    this.navigate({ ...this.state(), filter: { ...this.state().filter, search: text || undefined }, page: 1 });
  }

  /** Back to the feed's own filter, keeping the reader's sort. */
  removeFilters(): void {
    this.navigate({ filter: { ...this.feed.defaultFilter }, sort: this.state().sort, page: 1 });
  }

  chooseSort(sort: NewsSort): void {
    if (sort !== this.state().sort) {
      this.navigate({ ...this.state(), sort, page: 1 });
    }
  }

  changePage(page: number): void {
    this.navigate({ ...this.state(), page });
    this.windowService.goToTop();
  }

  ngOnDestroy(): void {
    [...this.loads, ...this.filterLoads].forEach((subscription) => subscription.unsubscribe());
  }

  private navigate(state: NewsFeedState): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: newsFeedQueryParams(state, this.feed) });
  }

  /**
   * Two requests, per the news API's featured filter: the featured items on
   * page 1, and the list without them, so nothing appears twice.
   */
  private load(state: NewsFeedState): void {
    this.loads.forEach((subscription) => subscription.unsubscribe());
    this.failed.set(false);

    this.setLoadingStatus('news:list', true);
    const list = this.newsService
      .getNews({ filter: { ...state.filter, featured: false }, sort: state.sort, page: state.page, pageSize: PAGE_SIZE, include: LISTING_RELATIONS })
      .pipe(finalize(() => this.setLoadingStatus('news:list', false)))
      .subscribe({
        next: (page) => this.newsPage.set(page),
        error: () => {
          this.newsPage.set(null);
          this.failed.set(true);
        },
      });

    this.loads = [list];

    if (state.page !== 1) {
      this.featured.set([]);
      return;
    }

    this.setLoadingStatus('news:featured', true);
    this.loads.push(
      this.newsService
        .getNews({ filter: { ...state.filter, featured: true }, sort: state.sort, pageSize: FEATURED_SHOWN, include: LISTING_RELATIONS })
        .pipe(finalize(() => this.setLoadingStatus('news:featured', false)))
        .subscribe({
          next: (page) => this.featured.set(page.items),
          error: () => this.featured.set([]),
        }),
    );
  }

  private loadFilters(): void {
    this.filterLoads.forEach((subscription) => subscription.unsubscribe());
    this.filterLoads = [];

    if (this.mayChooseApplication) {
      this.filterLoads.push(
        this.topicService.requestTopics('interculturalist', 'platform').subscribe({
          next: (topics) => this.applications.set(topics.filter((t) => t.category === 'interculturalist' && t.type === 'platform')),
          error: () => this.applications.set([]),
        }),
      );
    }

    if (mayChoose(this.feed, 'tag') && this.feed.tagTypes.length) {
      this.filterLoads.push(
        this.tagService.getTagsByType(this.feed.tagTypes, false).subscribe({
          next: (tags: Tag[]) =>
            this.tagGroups.set(
              this.feed.tagTypes
                .map((type) => ({ type, tags: tags.filter((t) => t.type === type) }))
                .filter((group) => group.tags.length),
            ),
          error: () => this.tagGroups.set([]),
        }),
      );
    }
  }

  private refreshCollapse(): void {
    this.areFiltersCollapsed.set(typeof window !== 'undefined' && window.matchMedia(this.maxWidthMediaQuery).matches);
  }

  private toContentItem(item: NewsItem): ContentItem {
    // A site whose feed is fixed to its own application would badge every card
    // with its own name; leave that one out.
    const fixed = this.mayChooseApplication ? [] : (this.feed.defaultFilter.application ?? []);
    const topics = item.topics
      .filter((topic) => !fixed.includes(topic.slug))
      .map((topic) => new Tag(topic.id, topic.name, topic.slug, 'topic', null, null, null));

    return new ContentItem(
      +item.id,
      item.title,
      null,
      null,
      null,
      this.plainText(item.summary),
      null,
      null,
      item.slug,
      null,
      null,
      // An item with no picture shows the news fallback on its card.
      item.images.length ? item.images : item.fallbackImage ? [item.fallbackImage] : [],
      null,
      item.videos[0] ?? null,
      null,
      null,
      item.publishedAt,
      [...topics, ...item.tags],
      null,
      {},
    );
  }

  /** The summary is Markdown; the card prints plain text. */
  private plainText(markdown: string | null): string {
    if (!markdown) {
      return '';
    }

    const html = this.markdown.render(markdown);

    return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';
  }
}
